import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import DataSyncForm from './components/DataSyncForm';
import { useObserver } from 'mobx-react-lite';
import { showToasts } from '../../lib/toaster';
import { getApiErrorMessage } from '../../lib/ZipApiError';
import Notification from './components/Notification';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { dataSyncStore } from '../../stores/DataSyncStore';
import { SegmentAnalytics } from '../../lib/SegmentAnalytics';
import { DataSyncItem } from '../../types/DataSyncItem';

const EditDataSyncPage = () => {
  const {dataSyncId} = useParams<{ dataSyncId: string }>();
  const history = useHistory();
  const [dataSync, setDataSync] = React.useState<DataSyncItem | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    dataSyncStore.getDataSync(dataSyncId)
      .then((obj) => {
        if (!cancelled) {
          setDataSync(obj);
        }
      })
      .catch(async (error: any) => {
        // Without this the rejection is unhandled and the page renders a spinner forever.
        const message = await getApiErrorMessage(error, 'Unable to load data sync');
        if (!cancelled) {
          setLoadError(message);
          showToasts([{intent: 'danger', message}]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [dataSyncId]);

  const onSubmit = async (dataSyncItem: DataSyncItem) => {
    await SegmentAnalytics.track('Data Sync Action', {
      action: 'Edit',
      path: window.location.pathname + window.location.search,
      dataSyncId: dataSyncItem.id
    });

    // Let errors propagate to useDataSyncSave, which owns the loading state and
    // surfaces the failure. Swallowing here leaves the save spinner stuck.
    await dataSyncStore.updateDataSync(dataSyncItem);
    history.push('/data_syncs');
    showToasts([{intent: 'success', message: 'Data sync updated successfully'}]);
  };

  const onCancel = () => {
    history.push('/data_syncs');
  };

  return useObserver(() => {
    if (loadError) {
      return <Notification message={loadError} show={true} type="error" />;
    }

    if (dataSyncStore.loading || !dataSync) {
      return <LoadingIndicator />;
    }

    return <DataSyncForm mode="edit" initialValues={dataSync} onSubmit={onSubmit} onCancel={onCancel}/>;
  });
};

export default EditDataSyncPage;
